import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, MapPin, CheckCircle2, Truck, Package, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/zafaraApi";
import { useShop } from "@/context/ShopContext";

const STEPS = [
  { key: "confirmed", label: "Confirmée", icon: CheckCircle2 },
  { key: "assigned", label: "Livreur attribué", icon: Package },
  { key: "picked_up", label: "Colis récupéré", icon: Package },
  { key: "in_delivery", label: "En livraison", icon: Truck },
  { key: "delivered", label: "Livrée", icon: CheckCircle2 },
];
const ORDER = ["pending", "confirmed", "assigned", "picked_up", "in_delivery", "delivered"];

export default function Suivi() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useShop();
  const [inputCode, setInputCode] = useState("");
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const t = await api.orderTracking(id);
      setTracking(t?.tracking || t);
    } catch (e) {
      if (e.status === 401) {
        setError("Connectez-vous pour suivre cette commande.");
      } else {
        setError(e.message || "Commande introuvable.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (orderId) load(orderId);
  }, [orderId, load]);

  // Poll every 20s if tracking is active
  useEffect(() => {
    if (!orderId || !tracking) return;
    const done = tracking.status === "delivered" || tracking.status === "cancelled";
    if (done) return;
    const iv = setInterval(() => load(orderId), 20000);
    return () => clearInterval(iv);
  }, [orderId, tracking, load]);

  const submit = (e) => {
    e.preventDefault();
    if (!inputCode.trim()) return;
    if (!user) {
      toast.info("Connectez-vous pour suivre une commande.");
      navigate(`/compte/connexion?next=/suivi/${inputCode.trim()}`);
      return;
    }
    navigate(`/suivi/${inputCode.trim()}`);
  };

  if (!orderId) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-16">
        <Helmet>
          <title>Suivi de livraison — Zafara Bénin</title>
          <meta name="robots" content="noindex,follow" />
        </Helmet>
        <div className="text-center">
          <MapPin className="h-10 w-10 mx-auto text-[#0E7A3B]" />
          <h1 className="mt-6 font-display text-4xl md:text-5xl text-[#16241B] tracking-tighter">
            Suivi de <span className="text-[#0E7A3B]">livraison</span>
          </h1>
          <p className="mt-3 text-[#16241B]/70">
            Entrez votre code de suivi ou identifiant de commande pour voir l'état de votre livraison en direct.
          </p>
        </div>
        <form onSubmit={submit} className="mt-10 rounded-3xl border border-[#EDE7D9] bg-white p-6">
          <label className="block">
            <span className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Code / ID de commande</span>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="suivi-code-input"
                value={inputCode}
                onChange={(e) => setInputCode(e.target.value)}
                placeholder="Ex : ABCD1234"
                className="flex-1 rounded-xl border border-[#EDE7D9] bg-[#FBFAF6] px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#0E7A3B]"
              />
              <button
                data-testid="suivi-code-submit"
                type="submit"
                className="inline-flex items-center gap-2 rounded-xl bg-[#0E7A3B] text-white px-5 py-3 font-semibold hover:bg-[#0d6a34]"
              >
                <Search className="h-4 w-4" /> Suivre
              </button>
            </div>
          </label>
        </form>
      </section>
    );
  }

  const currentIdx = tracking ? Math.max(0, ORDER.indexOf(tracking.status)) : -1;

  return (
    <section className="mx-auto max-w-3xl px-5 sm:px-8 py-10 md:py-16">
      <Helmet>
        <title>Suivi de commande — Zafara Bénin</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <button
        onClick={() => navigate("/suivi")}
        className="text-sm text-[#16241B]/70 hover:text-[#0E7A3B] mb-4 inline-flex items-center gap-1"
      >← Rechercher une autre commande</button>

      <h1 className="font-display text-3xl md:text-4xl text-[#16241B] tracking-tight">
        Suivi de commande
      </h1>
      <p className="mt-2 font-mono text-sm text-[#16241B]/70">#{String(orderId).slice(0, 12).toUpperCase()}</p>

      {loading && (
        <div className="mt-10 py-16 flex justify-center text-[#16241B]/60"><Loader2 className="h-6 w-6 animate-spin" /></div>
      )}
      {error && !loading && (
        <div className="mt-8 rounded-2xl border border-[#E8112D]/30 bg-[#E8112D]/5 p-6 text-[#E8112D]">
          {error}
        </div>
      )}

      {tracking && !loading && (
        <div className="mt-10 rounded-3xl border border-[#EDE7D9] bg-white p-6 md:p-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Statut actuel</p>
              <p data-testid="suivi-status" className="mt-1 font-display font-bold text-xl text-[#0E7A3B]">
                {STEPS.find((s) => s.key === tracking.status)?.label || tracking.status || "En attente"}
              </p>
            </div>
            {tracking.estimated_arrival && (
              <div className="text-right">
                <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Arrivée estimée</p>
                <p className="mt-1 font-display font-semibold text-[#16241B]">{tracking.estimated_arrival}</p>
              </div>
            )}
          </div>

          <ol className="relative">
            {STEPS.map((step, i) => {
              const done = i <= currentIdx;
              const Icon = step.icon;
              return (
                <li key={step.key} className="pl-10 pb-6 relative">
                  {i < STEPS.length - 1 && (
                    <span
                      className="absolute left-3 top-6 bottom-0 w-0.5"
                      style={{ backgroundColor: done ? "#0E7A3B" : "#EDE7D9" }}
                    />
                  )}
                  <span
                    className="absolute left-0 top-0 h-6 w-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: done ? "#0E7A3B" : "#EDE7D9", color: done ? "#fff" : "#16241B" }}
                  >
                    <Icon className="h-3 w-3" />
                  </span>
                  <p className={`font-display font-semibold ${done ? "text-[#16241B]" : "text-[#16241B]/50"}`}>
                    {step.label}
                  </p>
                </li>
              );
            })}
          </ol>

          {tracking.driver_name && (
            <div className="mt-4 rounded-2xl bg-[#FBFAF6] border border-[#EDE7D9] p-4">
              <p className="font-utility text-[10px] uppercase tracking-widest text-[#16241B]/60">Livreur</p>
              <p className="mt-1 font-display font-semibold text-[#16241B]">{tracking.driver_name}</p>
              {tracking.driver_phone && (
                <a href={`tel:${tracking.driver_phone}`} className="text-sm text-[#0E7A3B] hover:underline">
                  {tracking.driver_phone}
                </a>
              )}
            </div>
          )}

          <p className="mt-4 text-xs text-[#16241B]/50">Mise à jour automatique toutes les 20 secondes.</p>
        </div>
      )}
    </section>
  );
}
